/**
 * TourOverlay - Visual overlay component for tour steps
 *
 * Creates a spotlight effect that highlights the target element by cloning it
 * above a semi-transparent overlay, and displays the step content in a positioned popover.
 */
import React, { useState } from 'react';
import { Box } from '@withjoy/joykit';
import { PopoverV2 } from '@withjoy/joykit';
import { useElementClone } from '@shared/utils/hooks/useElementClone';
import { useMount } from '@shared/utils/hooks/useMount';

export type TourOverlayProps = Readonly<{
  targetRect: DOMRect;
  targetElement?: Maybe<HTMLElement>;
  children?: React.ReactNode;
  id?: string;
}>;

export const TourOverlay: React.FC<TourOverlayProps> = ({ targetRect, targetElement, children, id }) => {
  /** As per figma design, we have to show highlight of the target element on top of the overlay. */
  const elementClone = useElementClone(targetElement);
  const [isOpen, setIsOpen] = useState(false);

  // Delay opening PopoverV2 until after anchor is positioned
  // This ensures Popper reads correct anchor position on first render
  useMount(() => {
    const rafId = requestAnimationFrame(() => {
      setIsOpen(true);
    });
    return () => cancelAnimationFrame(rafId);
  });

  return (
    <Box
      id={id}
      __css={{ position: 'fixed', inset: 0, zIndex: 'popover', pointerEvents: 'none' }}
    >
      {/* Cloned element positioned above the overlay */}
      {targetElement && (
        <Box
          position="fixed"
          left={targetRect.left}
          top={targetRect.top}
          width={targetRect.width}
          height={targetRect.height}
          overflow="hidden"
          pointerEvents="none"
          zIndex={'skipLink'}
        >
          <Box
            ref={elementClone.ref}
            position="absolute"
            left={0}
            top={0}
            width="100%"
            height="100%"
            pointerEvents="none"
          />
        </Box>
      )}

      {/* Popover with tour content */}
      <PopoverV2
        isOpen={isOpen}
        placement={'auto'}
        triggerType="none"
        showArrow={true}
      >
        <PopoverV2.Anchor>
          <Box
            position="fixed"
            left={targetRect.left}
            top={targetRect.top}
            width={targetRect.width}
            height={targetRect.height}
            pointerEvents="none"
          />
        </PopoverV2.Anchor>
        <PopoverV2.Content
          role="dialog"
          aria-modal="true"
          aria-label="Tour step"
          tabIndex={-1}
          pointerEvents="auto"
          maxWidth={{ _: 'min(420px, calc(100vw - 24px))', md: '420px' }}
          borderRadius={12}
          background="white"
          outline="none"
          zIndex="toast"
        >
          {children}
        </PopoverV2.Content>
      </PopoverV2>
    </Box>
  );
};
